import React, { useEffect, useRef, useState } from 'react';
import { CloseIcon } from '../shared/icons';
import { useLessonAccess } from '../../hooks/useLessonAccess';
import { cn } from '../../lib/utils';

/**
 * Password box for the private lesson patterns, opened from "Sign in" in the header's
 * info menu. Modal: focus moves to the password field and stays in the dialog, Escape,
 * the X, Cancel or a click on the backdrop closes it, and focus goes back where it was.
 *
 * Only says "Sign in" anywhere a visitor can see it; what it unlocks is not named.
 */

const MESSAGES = {
  wrong_password: "That password didn't work.",
  not_configured: "Sign in isn't set up on this site yet.",
  network: "Couldn't reach ScaleBase. Check your connection and try again.",
};

const FOCUSABLE = 'button:not([disabled]), input:not([disabled]):not([tabindex="-1"])';

export default function SignInDialog({ open, onClose }) {
  const { signIn } = useLessonAccess();
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const dialogRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const returnTo = document.activeElement;
    setPassword('');
    setError(null);
    setBusy(false);
    // After the menu that opened us has finished putting focus back on its trigger.
    const timer = setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      clearTimeout(timer);
      if (returnTo && typeof returnTo.focus === 'function') returnTo.focus();
    };
  }, [open]);

  if (!open) return null;

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key !== 'Tab') return;
    const items = Array.from(dialogRef.current.querySelectorAll(FOCUSABLE));
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!password || busy) return;
    setBusy(true);
    setError(null);
    try {
      await signIn(password);
      onClose();
    } catch (err) {
      setError(MESSAGES[err.reason] ?? MESSAGES.network);
      setBusy(false);
      inputRef.current?.select();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 px-4 pt-[15vh]"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sign-in-title"
        onKeyDown={onKeyDown}
        className="w-full max-w-[360px] rounded-xl border border-line bg-tile p-4 pr-3 text-ink shadow-lg"
      >
        <div className="flex items-start justify-between gap-3">
          <h2 id="sign-in-title" className="pt-2 text-[18px] font-extrabold leading-snug">
            Sign in
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className={cn(
              '-mr-1 w-11 h-11 shrink-0 flex items-center justify-center rounded-[6px]',
              'text-ink-muted hover:text-ink hover:bg-root/10 transition-colors',
              'outline-none focus-visible:ring-2 focus-visible:ring-root',
            )}
          >
            <CloseIcon />
          </button>
        </div>

        <form onSubmit={onSubmit} className="mt-2 pr-1">
          {/* Lets password managers file the saved password under a name. */}
          <input
            type="text"
            name="username"
            autoComplete="username"
            value="scalebase"
            readOnly
            tabIndex={-1}
            aria-hidden="true"
            className="sr-only"
          />
          <label htmlFor="sign-in-password" className="block text-[14px] font-bold text-ink-muted">
            Password
          </label>
          <input
            ref={inputRef}
            id="sign-in-password"
            type="password"
            name="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            aria-invalid={error ? 'true' : undefined}
            aria-describedby={error ? 'sign-in-error' : undefined}
            className={cn(
              // 16px: iOS zooms the page into any smaller input.
              'mt-1 block w-full h-11 rounded-lg border border-line bg-paper px-3 text-[16px] text-ink',
              'outline-none focus-visible:ring-2 focus-visible:ring-root',
              // Safari draws its own caps-lock badge inside password fields; it clashes here.
              '[&::-webkit-caps-lock-indicator]:hidden [&::-webkit-caps-lock-indicator]:[content:none]',
            )}
          />
          <p
            id="sign-in-error"
            role="alert"
            className="mt-2 min-h-[20px] text-[14px] font-semibold leading-5 text-root-text"
          >
            {error}
          </p>
          <div className="mt-2 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className={cn(
                'h-11 px-3 rounded-lg text-[15px] font-semibold text-ink-muted',
                'hover:text-ink hover:bg-root/10 transition-colors',
                'outline-none focus-visible:ring-2 focus-visible:ring-root',
              )}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!password || busy}
              className={cn(
                'h-11 px-4 rounded-lg bg-root text-primary-foreground text-[15px] font-bold',
                'hover:bg-root/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
                'outline-none focus-visible:ring-2 focus-visible:ring-root focus-visible:ring-offset-2 focus-visible:ring-offset-tile',
              )}
            >
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
